/* eslint-disable react/no-unescaped-entities */
/**
 * Read-aloud player — single warm male voice (Adam / Kokoro), prefetched
 * the moment the lesson mounts so the student's first click is near-
 * instant whenever the clip is already cached (which it will be for
 * every lesson after the admin runs the Narration warmup).
 *
 * Primary: fal.ai Kokoro TTS via POST /api/tts/generate → <audio> playback.
 * Fallback: browser SpeechSynthesis on 402 / 5xx / network error so
 *   students never get a broken "Listen" button.
 */
import { useEffect, useRef, useState } from "react";
import { Headphones, Pause, Play, Square } from "lucide-react";
import { ttsApi } from "./shell";

const SPEEDS = [
  { v: 0.75, l: "0.75×" },
  { v: 1.0, l: "1×" },
  { v: 1.25, l: "1.25×" },
  { v: 1.5, l: "1.5×" },
];

// Single server-selected voice. If this ever needs to change the UI
// doesn't move — only the server-side default.
const VOICE = "am_adam";

function supportsBrowserTts() {
  return typeof window !== "undefined"
    && "speechSynthesis" in window
    && typeof window.SpeechSynthesisUtterance === "function";
}

function playBrowserFallback(text, rate, onEnd) {
  if (!supportsBrowserTts()) return null;
  const synth = window.speechSynthesis;
  synth.cancel();
  const u = new window.SpeechSynthesisUtterance(text);
  u.rate = rate;
  u.pitch = 1.0;
  u.lang = "en-US";
  u.onend = onEnd;
  u.onerror = onEnd;
  synth.speak(u);
  return u;
}

export function ReadAloud({ text, testIdSuffix = "" }) {
  const [state, setState] = useState("idle");      // "idle" | "loading" | "playing" | "paused"
  const [rate, setRate] = useState(1.0);
  const [prefetchStatus, setPrefetchStatus] = useState("idle"); // "idle" | "loading" | "ready" | "failed"
  const [usingFallback, setUsingFallback] = useState(false);
  const [err, setErr] = useState("");
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  // Holds the in-flight prefetch promise so a Listen click that lands
  // mid-prefetch can `await` the same request instead of firing another.
  const prefetchPromiseRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Fire the prefetch only when this lesson's "Listen" strip actually
  // enters the viewport (plus a 1024px rootMargin so prefetch starts a
  // little before the user scrolls to it — click still feels instant).
  useEffect(() => {
    if (typeof window === "undefined" || !containerRef.current) return;
    if (!("IntersectionObserver" in window)) { setInView(true); return; }
    const el = containerRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setInView(true); io.disconnect(); break; }
        }
      },
      { rootMargin: "1024px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Prefetch audio once the strip has (nearly) scrolled into view.
  // Fire-and-forget: whether this succeeds or fails the UI still works.
  useEffect(() => {
    if (!inView || !text || !text.trim()) return;
    let cancelled = false;
    setPrefetchStatus("loading");
    const promise = (async () => {
      try {
        const { audio_url } = await ttsApi.generate({ text, voice: VOICE });
        if (cancelled) return null;
        const abs = ttsApi.resolveAudioUrl(audio_url);
        if (!audioRef.current) audioRef.current = new Audio();
        audioRef.current.preload = "auto";
        audioRef.current.src = abs;
        audioRef.current.playbackRate = rate;
        audioRef.current.load();
        setPrefetchStatus("ready");
        return abs;
      } catch (e) {
        if (!cancelled) setPrefetchStatus("failed");
        throw e;
      }
    })();
    prefetchPromiseRef.current = promise;
    return () => {
      cancelled = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if (supportsBrowserTts()) window.speechSynthesis.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, inView]);

  if (!text || !text.trim()) return null;

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (supportsBrowserTts()) window.speechSynthesis.cancel();
    setState("idle");
    setUsingFallback(false);
  };

  const play = async () => {
    // Resume paused natural-voice playback.
    if (state === "paused" && audioRef.current && !usingFallback) {
      await audioRef.current.play();
      setState("playing");
      return;
    }
    // Resume paused browser fallback.
    if (state === "paused" && usingFallback && supportsBrowserTts()) {
      window.speechSynthesis.resume();
      setState("playing");
      return;
    }
    setErr("");

    // Mid-prefetch click: await the same request so we don't fire two
    // generations for one Listen.
    if (prefetchStatus === "loading" && prefetchPromiseRef.current) {
      setState("loading");
      try {
        await prefetchPromiseRef.current;
      } catch {
        // Prefetch failed — fall through to the slow path which will
        // either retry via generate() or fall back to browser TTS.
      }
    }

    // Fast path: natural-voice audio already cached in <audio>.
    if ((prefetchStatus === "ready" || audioRef.current?.src) && audioRef.current?.src) {
      try {
        audioRef.current.playbackRate = rate;
        audioRef.current.onended = () => setState("idle");
        audioRef.current.onerror = () => {
          setErr("Audio playback failed");
          setState("idle");
        };
        await audioRef.current.play();
        setUsingFallback(false);
        setState("playing");
        return;
      } catch {
        // Fall through — rare autoplay-blocked case.
      }
    }

    // Slow path: no prefetch ever landed. Generate on demand.
    setState("loading");
    try {
      const { audio_url } = await ttsApi.generate({ text, voice: VOICE });
      const absUrl = ttsApi.resolveAudioUrl(audio_url);
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = absUrl;
      audioRef.current.playbackRate = rate;
      audioRef.current.onended = () => setState("idle");
      audioRef.current.onerror = () => {
        setErr("Audio playback failed");
        setState("idle");
      };
      await audioRef.current.play();
      setPrefetchStatus("ready");
      setUsingFallback(false);
      setState("playing");
    } catch (e) {
      // 402 / 5xx / network → fall back to the free browser voice so the
      // student still gets lesson narration (just less natural).
      if (supportsBrowserTts()) {
        setUsingFallback(true);
        playBrowserFallback(text, rate, () => setState("idle"));
        setState("playing");
      } else {
        setErr(e.message || "Natural voice unavailable");
        setState("idle");
      }
    }
  };

  const pause = () => {
    if (usingFallback && supportsBrowserTts()) {
      window.speechSynthesis.pause();
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
    setState("paused");
  };

  const onRateChange = (e) => {
    const next = parseFloat(e.target.value);
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
    if (usingFallback && state === "playing") {
      window.speechSynthesis.cancel();
      playBrowserFallback(text, next, () => setState("idle"));
    }
  };

  const listenLabel = state === "loading"
    ? "Loading…"
    : state === "paused"
      ? "Resume"
      : prefetchStatus === "loading"
        ? "Listen"              // button still clickable; click triggers slow path
        : "Listen";

  return (
    <div className="tbg-tts-wrap" ref={containerRef}>
      <div className="tbg-tts" data-testid={`read-aloud${testIdSuffix}`} role="group" aria-label="Read lesson aloud">
        <Headphones size={13} color="#7b8b87" style={{ marginLeft: 4 }} />
        {state === "playing" ? (
          <button type="button" onClick={pause} className="active" data-testid={`tts-pause${testIdSuffix}`}>
            <Pause size={11} /> Pause
          </button>
        ) : (
          <button
            type="button" onClick={play}
            disabled={state === "loading"}
            className={prefetchStatus === "ready" ? "ready" : ""}
            data-testid={`tts-play${testIdSuffix}`}
          >
            <Play size={11} /> {listenLabel}
            {prefetchStatus === "ready" && state === "idle" && (
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block", width: 6, height: 6, marginLeft: 2,
                  borderRadius: 999, background: "#3dc0af",
                  boxShadow: "0 0 6px rgba(61,192,175,0.6)",
                }}
              />
            )}
          </button>
        )}
        <button
          type="button" onClick={stop} disabled={state === "idle" || state === "loading"}
          data-testid={`tts-stop${testIdSuffix}`}
        >
          <Square size={11} /> Stop
        </button>
        <select
          value={rate} onChange={onRateChange}
          data-testid={`tts-speed${testIdSuffix}`} aria-label="Playback speed"
        >
          {SPEEDS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>
      {usingFallback && (
        <div style={{ marginTop: 6, fontSize: 10, color: "#f0be3c",
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.06em" }}>
          Natural voice unavailable — using browser voice.
        </div>
      )}
      {err && !usingFallback && (
        <div style={{ marginTop: 6, fontSize: 10, color: "#f47171",
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.06em" }}>
          {err}
        </div>
      )}
    </div>
  );
}

/**
 * Strip markdown markup so the TTS reads clean prose instead of
 * "asterisk asterisk bold asterisk asterisk".
 */
export function stripMarkdownForTts(md) {
  if (!md) return "";
  let s = md;
  s = s.replace(/!\[[^\]]*\]\([^)]+\)/g, " ");          // images
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");        // links
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");              // bold
  s = s.replace(/\*([^*]+)\*/g, "$1");                  // italic
  s = s.replace(/`([^`]+)`/g, "$1");                    // inline code
  s = s.replace(/^#{1,6}\s+/gm, "");                    // headings
  s = s.replace(/^\s*[-*]\s+/gm, "");                   // bullets
  s = s.replace(/^\s*\d+\.\s+/gm, "");                  // numbered lists
  s = s.replace(/\n{2,}/g, "\n\n");
  return s.trim();
}
